import { PropsWithChildren, useContext, useEffect, useState } from 'react';

import { FluxProvider, TextLink } from '@upside/flux-ui';
import { StandardEvents } from '@upside/spa-shared-context';
import { useDarkMode } from '@upside/ui-hooks-lib';

import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppContext } from 'context/App';
import { useTranslation } from 'react-i18next';

import { i18n } from 'i18n';

import { changeLanguage, getLogoUrl } from './utils';

export function BgOverlay() {
  return (
    <div
      style={{ height: '250rem', width: '250rem' }}
      className="absolute bottom-1/3 left-1/2 z-0 -translate-x-1/2 rounded-full bg-neutral-5 dark:bg-neutral-95"
    />
  );
}

export function Content({
  children,
  loading,
}: PropsWithChildren<{ loading?: boolean }>) {
  if (loading)
    return (
      <div className="grid h-full items-center justify-center">
        <FontAwesomeIcon
          className="animate-spin text-purple-70 dark:text-purple-50"
          icon={faSpinnerThird}
          size="2x"
        />
      </div>
    );
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  );
}

export function Footer() {
  const { t: translate } = useTranslation();
  const { eventBus } = useContext(AppContext) || {};

  const [currentLang, setCurrentLang] = useState(i18n.resolvedLanguage);

  const updateBodyClass = (lang: string) => {
    const currentClass = document.body.className;
    const hasLangClass = currentClass.includes('i18n-');
    if (hasLangClass) {
      const currentLangClass = currentClass.match(/i18n-\w+/)?.[0];
      document.body.classList.remove(currentLangClass || '');
    }
    document.body.classList.add(`i18n-${lang}`);
  };

  useEffect(() => {
    if (currentLang) updateBodyClass(currentLang);
  }, [currentLang]);

  useEffect(() => {
    eventBus?.on(StandardEvents.ChangeLanguage, setCurrentLang);
  }, [eventBus]);

  return (
    <footer>
      <ul className="grid h-10 grid-flow-col items-end justify-center gap-3 text-sm font-medium text-foreground">
        <li>
          <TextLink
            asChild
            className="text-foreground decoration-foreground hover:text-foreground hover:decoration-foreground"
          >
            <a href="https://www.krakenflex.com/privacy-policy/">
              <TextLink.Label>{translate('privacyPolicy')}</TextLink.Label>
            </a>
          </TextLink>
        </li>
        <li className="border-l border-foreground pl-3">
          <TextLink
            asChild
            className="text-foreground decoration-foreground hover:text-foreground hover:decoration-foreground"
          >
            <a href="https://www.krakenflex.com/terms-and-conditions/">
              <TextLink.Label>{translate('termsAndConditions')}</TextLink.Label>
            </a>
          </TextLink>
        </li>
        <li className="border-l border-foreground pl-3">
          <TextLink
            className="text-foreground decoration-foreground hover:text-foreground hover:decoration-foreground"
            onClick={() =>
              changeLanguage(eventBus, currentLang === 'en' ? 'ja' : 'en')
            }
          >
            {translate('language')}
          </TextLink>
        </li>
      </ul>
    </footer>
  );
}

export function Layout({
  children,
  loading,
}: PropsWithChildren<{ loading?: boolean }>) {
  const {
    themeConfig: { name = '' },
  } = useContext(AppContext) ?? { themeConfig: {} };

  const { isDarkModeEnabled } = useDarkMode({
    targetSelectors: ['body', '#login-inner'],
  });

  const [ref, setContainerRef] = useState<HTMLDivElement | null>(null);

  return (
    <FluxProvider container={ref}>
      <div id="login" ref={setContainerRef}>
        <div
          id="login-inner"
          className="relative h-screen overflow-y-auto overflow-x-hidden bg-neutral-10 dark:bg-neutral-85"
        >
          <BgOverlay />
          <div className="z-10 flex min-h-full flex-col items-center justify-between p-10">
            <img
              alt={`${name} Logo`}
              className="z-10 h-14"
              src={getLogoUrl(name, isDarkModeEnabled)}
            />
            <Content loading={loading}>{children}</Content>
            <Footer />
          </div>
        </div>
      </div>
    </FluxProvider>
  );
}
